import * as Sentry from "@sentry/nuxt"
import { scopeKey } from "~/utils/address"

const getBackendErrorMessage = (error: any, fallbackMessage: string) => {
	const responseData = error?.response?._data ?? error?.response?.data
	const errorData = error?._data ?? error?.data
	const payload = responseData ?? errorData

	if (typeof payload === "string" && payload.trim()) {
		return payload
	}

	if (payload && typeof payload === "object") {
		if (typeof payload.message === "string" && payload.message.trim()) {
			return payload.message
		}
		if (typeof payload.error === "string" && payload.error.trim()) {
			return payload.error
		}
		if (Array.isArray(payload.errors) && payload.errors.length > 0) {
			const firstError = payload.errors[0]
			if (typeof firstError === "string" && firstError.trim()) {
				return firstError
			}
			if (firstError && typeof firstError.message === "string" && firstError.message.trim()) {
				return firstError.message
			}
		}
	}

	if (typeof error?.message === "string" && error.message.trim()) {
		return error.message
	}

	return fallbackMessage
}

const authHeaders = () => ({
	accept: "application/json",
	"x-access-token-user": useCookie("auth_token").value ?? "",
	"content-type": "application/json",
})

// Makers (non-admin) hit their own maker-scoped endpoint — the backend forces
// entity_type='maker'/entity_id=<their own makerId>/type='pickup' from the auth
// token regardless of what's sent, and 404s any attempt to reach another
// maker's or an org's address. Admins keep using the generic, entity-agnostic
// endpoint, unchanged.
const addressesBaseUrl = (rootState: any) =>
	rootState.isAdmin
		? `${import.meta.env.VITE_AND_DINE_API}/api/users/addresses`
		: `${import.meta.env.VITE_AND_DINE_API}/api/users/makers/addresses`

const handleWriteError = (
	commit: any,
	dispatch: any,
	error: any,
	description: string,
	fallback: string
) => {
	const errorMessage = getBackendErrorMessage(error, fallback)
	if (error?.response?.status === 401 || error?.response?.status === 403) {
		dispatch("handleError", { error, description }, { root: true })
	} else {
		commit("SET_ERROR_MESSAGE", errorMessage, { root: true })
	}
	Sentry.captureException(error)
	throw new Error(errorMessage)
}

const flashSuccess = (commit: any, message: string) => {
	commit("SET_SUCCESS_MESSAGE", message, { root: true })
	setTimeout(() => commit("SET_SUCCESS_MESSAGE", false, { root: true }), 5000)
}

// Generic addresses module — same shared `addresses` table backs maker pickup
// addresses, organisation delivery/billing addresses, customer delivery
// addresses (entity_type/entity_id/type). State is keyed by scope
// ("maker:<id>:pickup") so two AddressList/AddressManager instances on the
// same page (e.g. organisation delivery + billing) never overwrite each
// other's list — see docs/decisions.md 2026-07-28.
const addressesModule = {
	namespaced: true,
	state: () => ({
		addressesByScope: {} as Record<string, any[]>,
		loadingByScope: {} as Record<string, boolean>,
		errorByScope: {} as Record<string, string | false>,
		// Remembers how each scope was last fetched, so a post-write refetch keeps
		// the same archived view instead of collapsing an archived list to live-only.
		includeArchivedByScope: {} as Record<string, boolean>,
	}),
	mutations: {
		SET_ADDRESSES_LIST(state: any, { scope, addresses }: { scope: string; addresses: any[] }) {
			state.addressesByScope = { ...state.addressesByScope, [scope]: addresses || [] }
		},
		SET_ADDRESSES_LOADING(state: any, { scope, loading }: { scope: string; loading: boolean }) {
			state.loadingByScope = { ...state.loadingByScope, [scope]: loading }
		},
		SET_ADDRESSES_ERROR(state: any, { scope, error }: { scope: string; error: string | false }) {
			state.errorByScope = { ...state.errorByScope, [scope]: error }
		},
		SET_ADDRESSES_INCLUDE_ARCHIVED(
			state: any,
			{ scope, includeArchived }: { scope: string; includeArchived: boolean }
		) {
			state.includeArchivedByScope = { ...state.includeArchivedByScope, [scope]: includeArchived }
		},
	},
	actions: {
		async getAddresses({ commit, dispatch, state, rootState }: any, payload: any) {
			const { entity_type, entity_id, type } = payload || {}
			// Nothing to scope a fetch to — bail before building a junk scope key and
			// an entity_type=undefined&entity_id=undefined query.
			if (!entity_type || entity_id == null) return []
			const scope = scopeKey({ entityType: entity_type, entityId: entity_id, type })
			// Fall back to the scope's last-known archived view: the post-write
			// refetches (createAddress etc.) don't pass includeArchived, and
			// without this they'd silently collapse an archived list to live-only.
			const includeArchived =
				(payload?.includeArchived ?? state?.includeArchivedByScope?.[scope]) || false
			const query = new URLSearchParams({
				entity_type,
				entity_id: String(entity_id),
				...(type ? { type } : {}),
				...(includeArchived ? { includeArchived: "true" } : {}),
			})

			commit("SET_ADDRESSES_LOADING", { scope, loading: true })
			commit("SET_ADDRESSES_ERROR", { scope, error: false })
			commit("SET_ADDRESSES_INCLUDE_ARCHIVED", { scope, includeArchived })

			try {
				const response = await $fetch(`${addressesBaseUrl(rootState)}?${query}`, {
					headers: authHeaders(),
				})
				commit("SET_ADDRESSES_LIST", { scope, addresses: response })
				return response
			} catch (error: any) {
				const errorMessage = getBackendErrorMessage(error, "Failed to load addresses")
				commit("SET_ADDRESSES_ERROR", { scope, error: errorMessage })

				if (error?.response?.status === 401 || error?.response?.status === 403) {
					dispatch("handleError", { error, description: "load addresses" }, { root: true })
				} else {
					commit("SET_ERROR_MESSAGE", errorMessage, { root: true })
				}

				Sentry.captureException(error)
				throw new Error(errorMessage)
			} finally {
				commit("SET_ADDRESSES_LOADING", { scope, loading: false })
			}
		},

		// Create/update/archive/setPrimary all refetch their scope from the
		// response's own entity_type/entity_id/type on success — never a local
		// list mutation, so the dropdown always reflects what the backend has.
		async createAddress({ commit, dispatch, rootState }: any, payload: any) {
			try {
				const response = await $fetch(addressesBaseUrl(rootState), {
					method: "POST",
					body: payload,
					headers: authHeaders(),
				})
				flashSuccess(commit, "Address successfully created")
				await dispatch("getAddresses", {
					entity_type: response.entity_type,
					entity_id: response.entity_id,
					type: response.type,
				})
				return response
			} catch (error) {
				return handleWriteError(
					commit,
					dispatch,
					error,
					"create address",
					"Failed to create address"
				)
			}
		},

		async updateAddress({ commit, dispatch, rootState }: any, payload: any) {
			const { id, ...body } = payload || {}
			try {
				const response = await $fetch(`${addressesBaseUrl(rootState)}/${id}`, {
					method: "PUT",
					body,
					headers: authHeaders(),
				})
				flashSuccess(commit, "Address successfully updated")
				await dispatch("getAddresses", {
					entity_type: response.entity_type,
					entity_id: response.entity_id,
					type: response.type,
				})
				return response
			} catch (error) {
				return handleWriteError(
					commit,
					dispatch,
					error,
					"update address",
					"Failed to update address"
				)
			}
		},

		async archiveAddress({ commit, dispatch, rootState }: any, id: string) {
			try {
				const response = await $fetch(`${addressesBaseUrl(rootState)}/${id}`, {
					method: "DELETE",
					headers: authHeaders(),
				})
				flashSuccess(commit, "Address successfully archived")
				await dispatch("getAddresses", {
					entity_type: response.entity_type,
					entity_id: response.entity_id,
					type: response.type,
				})
				return response
			} catch (error) {
				return handleWriteError(
					commit,
					dispatch,
					error,
					"archive address",
					"Failed to archive address"
				)
			}
		},

		async setPrimaryAddress({ commit, dispatch, rootState }: any, id: string) {
			try {
				const response = await $fetch(`${addressesBaseUrl(rootState)}/${id}/primary`, {
					method: "PUT",
					headers: authHeaders(),
				})
				flashSuccess(commit, "Address successfully set as default")
				await dispatch("getAddresses", {
					entity_type: response.entity_type,
					entity_id: response.entity_id,
					type: response.type,
				})
				return response
			} catch (error) {
				return handleWriteError(
					commit,
					dispatch,
					error,
					"set default address",
					"Failed to set default address"
				)
			}
		},
	},
	getters: {
		addressesFor: (state: any) => (scope: string) => state.addressesByScope[scope] || [],
		addressesLoadingFor: (state: any) => (scope: string) => !!state.loadingByScope[scope],
		addressesErrorFor: (state: any) => (scope: string) => state.errorByScope[scope] || false,
	},
}

export default addressesModule
